import React from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalBody, ModalHeader, Col, Row, ModalFooter, Form } from 'reactstrap';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import FormInput from '@/components/forms/FormInput';
import { CountriesFilterLocal } from '../countries-query-reducer';

interface CountriesFilterFormProps {
  onClose: () => void;
  onApplyFilters: (filters: CountriesFilterLocal) => void;
  currentFilters?: CountriesFilterLocal;
}

/**
 * Filter modal for Countries table
 * Allows filtering by search, country code, region, subdivisions, etc.
 */
const CountriesFilter: React.FC<CountriesFilterFormProps> = ({ onClose, onApplyFilters, currentFilters }) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      search: currentFilters?.search,
      code: currentFilters?.code,
      regionName: currentFilters?.regionName,
      hasSubdivisions: currentFilters?.hasSubdivisions,
      subdivisionName: currentFilters?.subdivisionName,
    },
  });

  const onSubmit = (data: any) => {
    // Normalize select strings -> boolean | ''
    const normalized = {
      ...data,
      hasSubdivisions:
        data.hasSubdivisions === '' ? '' : data.hasSubdivisions === 'true' || data.hasSubdivisions === true,
    };

    // Clean up empty/undefined values
    const cleanedFilters = Object.entries(normalized).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as CountriesFilterLocal);

    onApplyFilters(cleanedFilters);
  };

  return (
    <Modal centered isOpen>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={onClose} className="d-flex justify-content-between align-items-center w-full">
          Country Filters
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col xs={12}>
              <FormInput label="Search" name="search" control={control} placeholder="Search by country name..." />
            </Col>
            <Col xs={12}>
              <FormInput label="Country Code" name="code" control={control} placeholder="e.g., US, CA, GB..." />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Region Name"
                name="regionName"
                control={control}
                placeholder="Filter by region assignment..."
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Has Subdivisions"
                name="hasSubdivisions"
                control={control}
                type="select"
                options={[
                  { label: 'All Countries', value: '' },
                  { label: 'With Subdivisions', value: 'true' },
                  { label: 'Without Subdivisions', value: 'false' },
                ]}
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Subdivision Name"
                name="subdivisionName"
                control={control}
                placeholder="Filter by subdivision name..."
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-outline"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            onClick={handleSubmit(onSubmit)}
          >
            Apply Filters
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CountriesFilter;
